import React from "react";
import PropTypes from "prop-types";
import { Avatar, Box, useTheme } from "@mui/material";
import {
  getCreditRemainingRatio,
  getCreditUsageStatus,
  getCreditUsageStatusColor,
} from "../../helper/creditBalanceModel";

const SIZE_PRESETS = {
  default: {
    ring: 42,
    avatar: 36,
    fontSize: 15,
    strokeWidth: 2.5,
  },
  compact: {
    ring: 32,
    avatar: 28,
    fontSize: 13,
    strokeWidth: 2,
  },
};

/**
 * Compact avatar with a subtle credit-usage ring for V2 navigation.
 */
export default function CreditUsageAvatar({
  initial,
  totalAvailable = null,
  monthlyAllowance = null,
  remainingRatio = null,
  status: statusProp = null,
  loading = false,
  error = null,
  size = "default",
  sx,
}) {
  const theme = useTheme();
  const { ring: RING_SIZE, avatar: AVATAR_SIZE, fontSize, strokeWidth: STROKE_WIDTH } =
    SIZE_PRESETS[size] || SIZE_PRESETS.default;
  const status =
    statusProp ||
    getCreditUsageStatus({ totalAvailable, monthlyAllowance });
  const ratio =
    Number.isFinite(remainingRatio)
      ? Math.max(0, Math.min(1, remainingRatio))
      : getCreditRemainingRatio({ totalAvailable, monthlyAllowance });
  const showTrack = !error;
  const showProgress = showTrack && !loading && totalAvailable !== null
    && status !== null && Number.isFinite(ratio);
  const radius = (RING_SIZE - STROKE_WIDTH) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - ratio);
  const ringColor = getCreditUsageStatusColor(theme.palette, status);

  return (
    <Box
      data-testid="credit-usage-avatar"
      sx={{
        position: "relative",
        width: RING_SIZE,
        height: RING_SIZE,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      {showTrack ? (
        <Box
          component="svg"
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          aria-hidden={!showProgress}
          aria-label={showProgress ? "Credits remaining indicator" : undefined}
          role={showProgress ? "img" : undefined}
          sx={{
            position: "absolute",
            inset: 0,
            transform: "rotate(-90deg)",
          }}
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={radius}
            fill="none"
            stroke={
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.12)"
                : "rgba(88, 89, 91, 0.14)"
            }
            strokeWidth={STROKE_WIDTH}
          />
          {showProgress ? (
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={radius}
              fill="none"
              stroke={ringColor}
              strokeWidth={STROKE_WIDTH}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
            />
          ) : null}
        </Box>
      ) : null}
      <Avatar
        sx={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          fontSize,
          fontWeight: 700,
          bgcolor: "primary.main",
        }}
      >
        {initial}
      </Avatar>
    </Box>
  );
}

CreditUsageAvatar.propTypes = {
  initial: PropTypes.string,
  totalAvailable: PropTypes.number,
  monthlyAllowance: PropTypes.number,
  remainingRatio: PropTypes.number,
  status: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.string,
  size: PropTypes.oneOf(["default", "compact"]),
  sx: PropTypes.object,
};
